/**
 * ResponseCard Component (Step 2 + Step 5)
 *
 * Presentational 2-row card for displaying response metadata with favorite star.
 * Header: Prompt preview + score + star + age
 * Body: Response preview
 * Footer: Comparison pill + response time + compact model identity
 *
 * Design: DS-compliant with Winner badge, Favorite star, A11y-ready
 * Plan: docs/03-responses_page_compact_card_surgical_implementation_plan.md Step 2 & 5
 */

import React from 'react';
import type { Comparison, Response as ComparisonResponse } from '../../../lib/types/results';
import { getModelLogo } from '../../../utils/logoMapping';
import { formatModelDisplayName } from '../../../utils/modelDisplayName';
import { getResponseContent } from '../../../utils/responseContent';
import { detectVisualElements } from '../../../utils/detectVisualElements';
import { VisualRenderer } from './VisualRenderer';

const normalizeLabel = (value?: string | null): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

export interface ResponseCardProps {
  response: ComparisonResponse;
  parentComparison?: Comparison;
  isWinner?: boolean;
  isFavorite?: boolean;
  onClick?: () => void;
  onOpenParent?: (comparisonId: string) => void;
  onFavoriteToggle?: (responseId: string) => void;
}

export const ResponseCard: React.FC<ResponseCardProps> = ({
  response,
  parentComparison,
  isWinner = false,
  isFavorite = false,
  onClick,
  onFavoriteToggle,
}) => {
  const fallbackDisplayName = formatModelDisplayName(response.modelId);
  const preferredDisplayName =
    normalizeLabel(response.model?.displayName) ??
    normalizeLabel(response.model?.name) ??
    normalizeLabel(response.modelName) ??
    fallbackDisplayName;
  const providerLabel =
    normalizeLabel(response.provider?.name) ??
    normalizeLabel(response.providerName) ??
    normalizeLabel(response.providerId) ??
    response.modelId;
  const modelDisplayName = preferredDisplayName;
  // Format age relative to now
  const formatAge = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Format response time in seconds
  const formatResponseTime = (ms?: number) => {
    if (!ms) return null;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const age = formatAge(response.createdAt);
  const responseTime = formatResponseTime(response.responseTime);
  const score = response.scoreOverall;
  const promptPreview = (() => {
    const fallbackSources = [parentComparison?.title, parentComparison?.prompt];
    for (const source of fallbackSources) {
      if (!source) continue;
      const normalized = source.replace(/\s+/g, ' ').trim();
      if (normalized.length > 0) {
        return normalized;
      }
    }
    return 'Prompt unavailable';
  })();
  const previewText = (() => {
    const content = getResponseContent(response).trim();
    return content.length > 0 ? content : 'No response preview available.';
  })();
  const formattedModelName = preferredDisplayName;

  // Detect visual elements
  const visualTypes = detectVisualElements(getResponseContent(response));

  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={`group relative bg-slate-800/30 backdrop-blur-sm border rounded-xl p-4 cursor-pointer transition-all duration-200 ${
        isWinner
          ? 'border-cyan-400/60 ring-2 ring-cyan-400/30 shadow-lg shadow-cyan-500/10'
          : 'border-slate-700/50 hover:border-cyan-500/30 hover:shadow-lg hover:shadow-cyan-500/10'
      }`}
      role="article"
      aria-label={`Response from ${modelDisplayName}${isWinner ? ' (Winner)' : ''}`}
      tabIndex={0}
    >
      {/* Header: Prompt Preview + Score + Star + Age */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white group-hover:text-cyan-300 transition-colors line-clamp-2">
              {promptPreview}
            </p>
          </div>
          {/* Winner Badge */}
          {isWinner && (
            <span
              className="inline-flex items-center space-x-1 px-1.5 py-0.5 bg-cyan-500/20 border border-cyan-400/40 rounded text-xs font-bold text-cyan-300 flex-shrink-0"
              aria-label="Winner"
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </span>
          )}
        </div>

        {/* Score + Star + Age */}
        <div className="flex items-center space-x-2 text-xs text-slate-400 flex-shrink-0 ml-2">
          {score !== null && score !== undefined && (
            <div className="flex items-center space-x-1">
              <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="font-semibold text-yellow-400">{score.toFixed(1)}</span>
            </div>
          )}

          {/* Favorite Star (Step 5) */}
          {onFavoriteToggle && (
            <button
              onClick={(e) => {
                e.stopPropagation(); // Don't trigger card click
                onFavoriteToggle(response.id);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  e.preventDefault();
                  onFavoriteToggle(response.id);
                }
              }}
              className="p-0.5 rounded transition-all duration-200 hover:bg-slate-700/50"
              aria-label={isFavorite ? 'Unfavorite response' : 'Favorite response'}
              aria-pressed={isFavorite}
            >
              <svg
                className={`w-4 h-4 transition-colors ${
                  isFavorite
                    ? 'fill-yellow-400 stroke-yellow-400'
                    : 'fill-none stroke-slate-500 group-hover:stroke-slate-400'
                }`}
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                />
              </svg>
            </button>
          )}

          <span>{age}</span>
        </div>
      </div>

      <div className="mb-3 text-sm text-slate-300">
        <div className="line-clamp-2 overflow-hidden">
          <VisualRenderer content={previewText} className="text-sm" />
        </div>
      </div>

      {/* Visual Element Badges */}
      {visualTypes.size > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {Array.from(visualTypes).map((type) => (
            <span
              key={type}
              className="text-xs px-2 py-0.5 bg-slate-700/50 text-slate-300 rounded border border-slate-600/30"
            >
              {type}
            </span>
          ))}
        </div>
      )}

      {/* Footer: Response Time + Model Identity */}
      <div className="mt-1 flex items-center text-xs text-slate-400 gap-3">
        <div className="flex-1 flex justify-center">
          {responseTime && <span className="text-slate-400">Response time: {responseTime}</span>}
        </div>

        <div className="flex items-center justify-end space-x-2 text-right">
          <div className="w-6 h-6 bg-slate-700/30 rounded-md flex items-center justify-center p-1">
            <img
              src={getModelLogo(response.modelId, response.providerId)}
              alt={`${formattedModelName} logo`}
              className="w-full h-full object-contain"
            />
          </div>
          <span className="font-medium text-slate-300">{formattedModelName}</span>
        </div>
      </div>
    </div>
  );
};
